import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { useTranslation, Trans } from 'react-i18next'
import './DigCompWheel.css'

/**
 * DigCompWheel — Interactive DigComp 3.0 framework explorer.
 *
 * Desktop layout (two rows):
 *   Row 1: Pentagon SVG (left) | Area list 1–5 (right)
 *   Row 2: Subcategories + scenario cards for the selected area
 *
 * Mobile layout:
 *   Pentagon SVG on top, then collapsible area accordions
 *   with subcategories and scenario cards inside each.
 *
 * @see GitHub Issue #408
 */

// Pentagon geometry
const CX = 300
const CY = 300
const OUTER_R = 220
const INNER_R = 60

const AREA_ANGLES = [
  -90,   // Area 1 — top
  -18,   // Area 2 — top-right
  54,    // Area 3 — bottom-right
  126,   // Area 4 — bottom-left
  198,   // Area 5 — top-left
]

function polarToCartesian(cx, cy, radius, angleDeg) {
  const angleRad = (angleDeg * Math.PI) / 180
  return {
    x: cx + radius * Math.cos(angleRad),
    y: cy + radius * Math.sin(angleRad),
  }
}

function buildPetalPath(startAngle, endAngle) {
  const midAngle = (startAngle + endAngle) / 2
  const innerStart = polarToCartesian(CX, CY, INNER_R, startAngle)
  const innerEnd = polarToCartesian(CX, CY, INNER_R, endAngle)
  const outerTip = polarToCartesian(CX, CY, OUTER_R, midAngle)
  const ctrl1 = polarToCartesian(CX, CY, OUTER_R * 0.85, startAngle + (endAngle - startAngle) * 0.15)
  const ctrl2 = polarToCartesian(CX, CY, OUTER_R * 0.85, startAngle + (endAngle - startAngle) * 0.85)

  return [
    `M ${innerStart.x} ${innerStart.y}`,
    `Q ${ctrl1.x} ${ctrl1.y} ${outerTip.x} ${outerTip.y}`,
    `Q ${ctrl2.x} ${ctrl2.y} ${innerEnd.x} ${innerEnd.y}`,
    `A ${INNER_R} ${INNER_R} 0 0 0 ${innerStart.x} ${innerStart.y}`,
    'Z',
  ].join(' ')
}

// Shared: renders subcategories + scenario cards for one area
function AreaDetail({ area, compMap, onStartScenario, loadingScenarioId, highlightScenarioId }) {
  const { t } = useTranslation()
  // Translate area/competence names via i18n keys (fallback to raw config value)
  const areaName = t(`digcomp.area${area.id}`, { defaultValue: area.name })
  return (
    <div className="dcw-detail">
      <h3 className="dcw-detail__heading" style={{ color: area.color }}>
        {areaName}
      </h3>
      <div className="dcw-detail__comps">
        {area.competences.map((comp) => {
          const scenariosForComp = compMap[comp.id] || []
          const hasScenario = scenariosForComp.length > 0
          const compName = t(`digcomp.comp${comp.id.replace('.', '_')}`, { defaultValue: comp.name })
          return (
            <div key={comp.id} className={`dcw-comp ${hasScenario ? '' : 'dcw-comp--empty'}`}>
              <div className="dcw-comp__header">
                <span className="dcw-comp__id" style={{ color: area.color }}>{comp.id}</span>
                <span className="dcw-comp__name">{compName}</span>
                {!hasScenario && <span className="dcw-comp__soon">{t('digcomp.comingSoon')}</span>}
              </div>
              {hasScenario && (
                <div className="dcw-comp__scenarios">
                  {scenariosForComp.map((scenario) => {
                    const isThisLoading = loadingScenarioId === scenario.id
                    return (
                      <button
                        key={scenario.id}
                        data-scenario-id={scenario.id}
                        className={`dcw-scenario ${isThisLoading ? 'dcw-scenario--loading' : ''} ${highlightScenarioId === scenario.id ? 'dcw-scenario--deeplink' : ''}`}
                        onClick={() => onStartScenario && onStartScenario(scenario)}
                        disabled={!!loadingScenarioId}
                      >
                        <div className="dcw-scenario__avatar">
                          {isThisLoading ? (
                            <div className="dcw-scenario__spinner" />
                          ) : scenario.avatar_url ? (
                            <img src={scenario.avatar_url} alt="" />
                          ) : (
                            <div className="dcw-scenario__avatar-ph" style={{ backgroundColor: area.color }}>
                              {(scenario.ai_role || scenario.name || '').charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="dcw-scenario__info">
                          <span className="dcw-scenario__title">{scenario.name}</span>
                          <span className="dcw-scenario__desc">{scenario.summary}</span>
                        </div>
                        <span className="dcw-scenario__arrow" aria-hidden="true">&rsaquo;</span>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

AreaDetail.propTypes = {
  area: PropTypes.object.isRequired,
  compMap: PropTypes.object.isRequired,
  onStartScenario: PropTypes.func,
  loadingScenarioId: PropTypes.number,
  highlightScenarioId: PropTypes.number,
}

function DigCompWheel({
  frameworkConfig,
  scenarios = [],
  onSelectArea,
  onStartScenario,
  selectedAreaId = null,
  loadingScenarioId = null,
  deepLinkScenarioId = null,
}) {
  const [hoveredArea, setHoveredArea] = useState(null)
  // Mobile accordion: which area is expanded (null = none)
  const [mobileExpanded, setMobileExpanded] = useState(null)
  const svgRef = useRef(null)

  const compMap = useMemo(() => {
    const map = {}
    for (const s of scenarios) {
      if (s.competence_code) {
        if (!map[s.competence_code]) map[s.competence_code] = []
        map[s.competence_code].push(s)
      }
    }
    return map
  }, [scenarios])

  const areaCounts = useMemo(() => {
    const counts = {}
    for (const area of (frameworkConfig?.areas || [])) {
      counts[area.id] = area.competences.reduce((sum, c) => sum + (compMap[c.id]?.length || 0), 0)
    }
    return counts
  }, [frameworkConfig, compMap])

  const handleAreaClick = useCallback((areaId) => {
    if (onSelectArea) {
      onSelectArea(selectedAreaId === areaId ? null : areaId)
    }
  }, [onSelectArea, selectedAreaId])

  const handleKeyDown = useCallback((e, action) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      action()
    }
  }, [])

  const toggleMobileAccordion = useCallback((areaId) => {
    setMobileExpanded(prev => prev === areaId ? null : areaId)
  }, [])

  // Deep-link (#scenario-<id>): expand the containing area's mobile accordion
  // (desktop area selection is the parent's job via selectedAreaId), then
  // scroll the visible card into view once it's in the DOM. Both desktop and
  // mobile render the same cards, so target the visible one via offsetParent
  // rather than an id (which would be duplicated).
  useEffect(() => {
    if (!deepLinkScenarioId) return
    const entry = Object.entries(compMap).find(([, list]) =>
      list.some(s => s.id === deepLinkScenarioId)
    )
    if (!entry) return
    setMobileExpanded(entry[0].split('.')[0])
    const timer = setTimeout(() => {
      const cards = document.querySelectorAll(`[data-scenario-id="${deepLinkScenarioId}"]`)
      const visible = Array.from(cards).find(el => el.offsetParent !== null)
      if (visible) visible.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 300)
    return () => clearTimeout(timer)
  }, [deepLinkScenarioId, compMap])

  const { t } = useTranslation()

  if (!frameworkConfig?.areas?.length) return null

  const areas = frameworkConfig.areas
  const activeArea = areas.find(a => a.id === selectedAreaId) || areas[0]

  // Shared SVG pentagon renderer
  const renderPentagon = (containerClass) => (
    <div className={containerClass}>
      <svg
        ref={svgRef}
        viewBox="0 0 600 600"
        className="dcw-svg"
        role="img"
        aria-label="DigComp 3.0 pentagon diagram"
      >
        <defs>
          {areas.map((area) => (
            <filter key={`glow-${area.id}`} id={`glow-${area.id}`} x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feFlood floodColor={area.color} floodOpacity="0.4" result="color" />
              <feComposite in="color" in2="blur" operator="in" result="shadow" />
              <feMerge>
                <feMergeNode in="shadow" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          ))}
        </defs>

        {areas.map((area, i) => {
          const startAngle = AREA_ANGLES[i] - 36
          const endAngle = AREA_ANGLES[i] + 36
          const path = buildPetalPath(startAngle, endAngle)
          const isHovered = hoveredArea === area.id
          const isSelected = selectedAreaId === area.id
          const isActive = isHovered || isSelected

          return (
            <g key={area.id}>
              <path
                d={path}
                className={`dcw-petal ${isActive ? 'active' : ''} ${areaCounts[area.id] === 0 ? 'empty' : ''}`}
                fill={area.color}
                fillOpacity={isActive ? 0.95 : 0.75}
                stroke={area.color}
                strokeWidth={isActive ? 2.5 : 1}
                filter={isActive ? `url(#glow-${area.id})` : undefined}
                onMouseEnter={() => setHoveredArea(area.id)}
                onMouseLeave={() => setHoveredArea(null)}
                onClick={() => handleAreaClick(area.id)}
                onKeyDown={(e) => handleKeyDown(e, () => handleAreaClick(area.id))}
                tabIndex={0}
                role="button"
                aria-label={`${t(`digcomp.area${area.id}Short`, { defaultValue: area.shortName })}: ${t(`digcomp.area${area.id}`, { defaultValue: area.name })}. ${t('digcomp.scenarioCount', { count: areaCounts[area.id] })}.`}
                style={{
                  cursor: 'pointer',
                  transition: 'fill-opacity 0.2s ease, stroke-width 0.2s ease, transform 0.2s ease',
                  transformOrigin: `${CX}px ${CY}px`,
                  transform: isActive ? 'scale(1.04)' : 'scale(1)',
                }}
              />
              {(() => {
                const labelPos = polarToCartesian(CX, CY, OUTER_R * 0.55, AREA_ANGLES[i])
                return (
                  <text
                    x={labelPos.x} y={labelPos.y}
                    className="dcw-petal-label"
                    textAnchor="middle" dominantBaseline="central"
                    fill="#fff" fontSize="14" fontWeight="600"
                    pointerEvents="none"
                    style={{ textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}
                  >
                    {t(`digcomp.area${area.id}Short`, { defaultValue: area.shortName })}
                  </text>
                )
              })()}
            </g>
          )
        })}

        <circle cx={CX} cy={CY} r={INNER_R - 2} fill="#fff" stroke="#e8e8ed" strokeWidth="2"
          className="dcw-centre"
          onClick={() => onSelectArea && onSelectArea(null)}
          onKeyDown={(e) => handleKeyDown(e, () => onSelectArea && onSelectArea(null))}
          tabIndex={0} role="button" aria-label={t('digcomp.showAllAreas')} style={{ cursor: 'pointer' }}
        />
        <text x={CX} y={CY - 8} textAnchor="middle" dominantBaseline="central" fontSize="16" fontWeight="700" fill="#1d1d1f" pointerEvents="none">DigComp</text>
        <text x={CX} y={CY + 12} textAnchor="middle" dominantBaseline="central" fontSize="11" fill="#86868b" pointerEvents="none">3.0</text>
      </svg>
    </div>
  )

  return (
    <div className="dcw" role="figure" aria-label={`${frameworkConfig.name} framework`}>

      {/* ===== DESKTOP (>= 768px) ===== */}
      <div className="dcw-desktop">
        {/* Row 1: Pentagon (left) | Area list (right) */}
        <div className="dcw-row1">
          {renderPentagon('dcw-row1__svg')}
          <div className="dcw-row1__areas" role="list" aria-label="Competence areas">
            {areas.map((area) => {
              const isSelected = (selectedAreaId || areas[0].id) === area.id
              return (
                <button
                  key={area.id}
                  className={`dcw-area-btn ${isSelected ? 'selected' : ''}`}
                  onClick={() => onSelectArea && onSelectArea(area.id)}
                  style={{ '--area-color': area.color }}
                  role="listitem"
                >
                  <span className="dcw-area-btn__num" style={{ backgroundColor: area.color }}>
                    {area.id}
                  </span>
                  <span className="dcw-area-btn__label">{t(`digcomp.area${area.id}`, { defaultValue: area.name })}</span>
                  {areaCounts[area.id] > 0 && (
                    <span className="dcw-area-btn__count" style={{ backgroundColor: area.color }}>
                      {t('digcomp.scenarioCount', { count: areaCounts[area.id] })}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* Row 2: Subcategories + scenario cards */}
        <div className="dcw-row2">
          <AreaDetail area={activeArea} compMap={compMap} onStartScenario={onStartScenario} loadingScenarioId={loadingScenarioId} highlightScenarioId={deepLinkScenarioId} />
        </div>
      </div>

      {/* ===== MOBILE (< 768px) ===== */}
      <div className="dcw-mobile">
        {renderPentagon('dcw-mobile__svg')}

        {/* Collapsible area accordions */}
        <div className="dcw-mobile__accordions">
          {areas.map((area) => {
            const isOpen = mobileExpanded === area.id
            return (
              <div key={area.id} className={`dcw-accordion ${isOpen ? 'open' : ''}`}>
                <button
                  className="dcw-accordion__trigger"
                  onClick={() => toggleMobileAccordion(area.id)}
                  aria-expanded={isOpen}
                  style={{ '--area-color': area.color }}
                >
                  <span className="dcw-accordion__num" style={{ backgroundColor: area.color }}>
                    {area.id}
                  </span>
                  <span className="dcw-accordion__label">{t(`digcomp.area${area.id}`, { defaultValue: area.name })}</span>
                  {areaCounts[area.id] > 0 && (
                    <span className="dcw-accordion__count" style={{ backgroundColor: area.color }}>
                      {areaCounts[area.id]}
                    </span>
                  )}
                  <span className={`dcw-accordion__chevron ${isOpen ? 'open' : ''}`} aria-hidden="true">&#8250;</span>
                </button>
                {isOpen && (
                  <div className="dcw-accordion__body">
                    <AreaDetail area={area} compMap={compMap} onStartScenario={onStartScenario} loadingScenarioId={loadingScenarioId} highlightScenarioId={deepLinkScenarioId} />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Attribution */}
      <p className="dcw-attribution">
        <Trans i18nKey="digcomp.attribution">
          Based on <a href="https://publications.jrc.ec.europa.eu/repository/handle/JRC128415" target="_blank" rel="noopener noreferrer">DigComp 3.0</a> by the European Commission, JRC
        </Trans>
      </p>
    </div>
  )
}

DigCompWheel.propTypes = {
  frameworkConfig: PropTypes.shape({
    type: PropTypes.string,
    name: PropTypes.string,
    source: PropTypes.string,
    areas: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      shortName: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      competences: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
      })).isRequired,
    })).isRequired,
  }).isRequired,
  scenarios: PropTypes.array,
  onSelectArea: PropTypes.func,
  onStartScenario: PropTypes.func,
  selectedAreaId: PropTypes.string,
  loadingScenarioId: PropTypes.number,
  deepLinkScenarioId: PropTypes.number,
}

export default DigCompWheel
